import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getAdminUsers } from '../api';
import { ChevronUp, ChevronDown } from 'lucide-react';

const Users = () => {
    const { excludeCompany } = useOutletContext();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
    const limit = 20;
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsers();
    }, [page, excludeCompany]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await getAdminUsers(page, limit, excludeCompany);
            if (res.data.success) {
                setUsers(res.data.data);
                setTotalPages(Math.ceil(res.data.total / limit));
            }
        } catch (error) {
            console.error("Failed to fetch users", error);
        } finally {
            setLoading(false);
        }
    };

    const sortedUsers = useMemo(() => {
        if (!users.length) return users;
        const sorted = [...users].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // Handle null/undefined
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';

            // Handle numeric values
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // Handle string values
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [users, sortConfig]);

    const requestSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const renderSortIcon = (key) => {
        const isActive = sortConfig.key === key;
        return (
            <div className="flex flex-col ml-1">
                <ChevronUp size={12} className={isActive && sortConfig.direction === 'asc' ? 'text-blue-600' : 'text-gray-300'} />
                <ChevronDown size={12} className={`-mt-1 ${isActive && sortConfig.direction === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
            </div>
        );
    };

    const formatStorage = (mb) => {
        if (!+mb) return '0 MB';
        if (mb >= 1000) {
            return `${(mb / 1000).toFixed(1)} GB`;
        }
        return `${Math.round(mb)} MB`;
    };

    const getPlanBadgeColor = (code) => {
        const c = (code || '').toLowerCase();
        if (c.includes('beta')) return 'bg-blue-100 text-blue-700';
        if (c.includes('free')) return 'bg-gray-100 text-gray-600';
        return 'bg-purple-100 text-purple-700';
    };

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-800">Users Management</h1>

            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center">Email{renderSortIcon('email')}</div>
                                </th>
                                <th onClick={() => requestSort('username')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center">Username{renderSortIcon('username')}</div>
                                </th>
                                <th onClick={() => requestSort('plan_name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center">Plan{renderSortIcon('plan_name')}</div>
                                </th>
                                <th onClick={() => requestSort('remaining_credits')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center justify-center">Credits (Rem/Tot){renderSortIcon('remaining_credits')}</div>
                                </th>
                                <th onClick={() => requestSort('remaining_storage')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center justify-center">Storage (Rem/Tot){renderSortIcon('remaining_storage')}</div>
                                </th>
                                <th onClick={() => requestSort('optimize_count')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center justify-center">Optimizations{renderSortIcon('optimize_count')}</div>
                                </th>
                                <th onClick={() => requestSort('created_at')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center">Joined{renderSortIcon('created_at')}</div>
                                </th>
                                <th onClick={() => requestSort('last_login')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                    <div className="flex items-center">Last Login{renderSortIcon('last_login')}</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">Loading...</td>
                                </tr>
                            ) : sortedUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">No users found.</td>
                                </tr>
                            ) : (
                                sortedUsers.map((user) => (
                                    <tr
                                        key={user.id}
                                        onClick={() => navigate(`/users/${user.id}`)}
                                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{user.email}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <span className="text-sm text-gray-500">{user.username}</span>
                                                <span className="text-xs text-gray-400">{user.company}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getPlanBadgeColor(user.plan_code)}`}>
                                                {user.plan_name}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-700">
                                            {user.remaining_credits?.toLocaleString()} <span className="text-gray-400">/</span> {user.total_credits?.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-700">
                                            {formatStorage(user.remaining_storage)} <span className="text-gray-400">/</span> {formatStorage(user.total_storage)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="flex flex-col space-y-1">
                                                <span className="text-xs text-gray-500">Opt: {user.optimize_count}</span>
                                                <span className="text-xs text-gray-500">Down: {user.download_count}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.created_at?.split(' ')[0]}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.last_login?.split(' ')[0] || "-"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                    Previous
                </button>
                <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
                <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default Users;
